/**
 * 动态指标映射生成器
 *
 * 根据标定数据源可用性动态生成指标映射。
 * 指标的勾选和组合遵循产品同学提供的特殊逻辑，
 * 用基于 M Science 和 VGI 标记的动态生成方式替代之前的硬编码指标常量。
 */

import { ICalibrationAvailability } from '../types/calibration';
import { getDataSourceScenario } from './calibrationParser';
import { SEGMENT_CASCADER_ALL_VALUE } from '@intelligence/_config/components/filters/segmentCascader';

/**
 * 根据标定数据生成所有指标映射
 *
 * 根据当前可用的数据源（M Science、VGI 或两者）为表格指标、可视化指标以及分段筛选器
 * 动态生成对应的指标映射配置。
 *
 * @param calibrationData 标定数据源可用性标记
 * @returns 包含所有动态指标映射的对象
 *
 * @example
 * ```typescript
 * const calibration = { is_mscience_daily: true, is_vgi_daily: false };
 * const mappings = generateMetricMapping(calibration);
 * // 仅返回 M Science 数据源对应的指标映射
 * ```
 */
export function generateMetricMapping(calibrationData: ICalibrationAvailability | null) {
  // 如果没有标定数据，默认按两个数据源都可用处理（只展示公共指标）
  if (!calibrationData) {
    console.warn('[DynamicMetricMapping] No calibration data provided. Using both-true defaults (common metrics).');
    calibrationData = { is_mscience_daily: true, is_vgi_daily: true };
  }

  const scenario = getDataSourceScenario(calibrationData);
  
  console.log(`[DynamicMetricMapping] Generating mappings for scenario: ${scenario}`);

  // 根据当前场景生成收入类指标列表
  const revenueMetrics = getMetricsByDataSource('revenue', scenario);
  const salesUnitsMetrics = getMetricsByDataSource('sales_units', scenario);
  
  // 生成可视化使用的指标列表
  const visualRevenueMetrics = getVisualizationMetrics('revenue', scenario);
  const visualSalesUnitsMetrics = getVisualizationMetrics('sales_units', scenario);

  // 构建表格指标映射关系
  const DATABRAIN_METRIC_MAPPING: Record<string, string[]> = {
    // Total Revenue mappings
    databrain__total_revenue: revenueMetrics,
    // Lifetime Total Revenue mappings
    databrain__lifetime_total_revenue: revenueMetrics,
    // Base Game Sales Units mappings
    databrain__base_game_sales_units: salesUnitsMetrics,
    // Lifetime Base Game Sales Units mappings
    databrain__lifetime_base_game_sales_units: salesUnitsMetrics,
  };

  // 构建可视化指标映射关系
  const DATABRAIN_VISUALIS_METRIC_MAPPING: Record<string, string[]> = {
    // Total Revenue visualization
    databrain__total_revenue: visualRevenueMetrics,
    // Lifetime Total Revenue visualization
    databrain__lifetime_total_revenue: visualRevenueMetrics,
    // Base Game Sales Units visualization
    databrain__base_game_sales_units: visualSalesUnitsMetrics,
    // Lifetime Base Game Sales Units visualization
    databrain__lifetime_base_game_sales_units: visualSalesUnitsMetrics,
  };

  // 构建销量分段筛选器与指标的映射关系
  const DATABRAIN_UNITS_METRICS_SALES_SEGMENT_MAPPING: Record<string, Record<string, string[]>> = {
    // Revenue metrics use all segments
    databrain__total_revenue: SEGMENT_CASCADER_ALL_VALUE,
    databrain__lifetime_total_revenue: SEGMENT_CASCADER_ALL_VALUE,
    // Sales units metrics use specific segments based on data source
    databrain__base_game_sales_units: getSegmentMapping(scenario),
    databrain__lifetime_base_game_sales_units: getSegmentMapping(scenario),
  };

  return {
    DATABRAIN_METRIC_MAPPING,
    DATABRAIN_VISUALIS_METRIC_MAPPING,
    DATABRAIN_UNITS_METRICS_SALES_SEGMENT_MAPPING,
  };
}

/**
 * 根据数据源与指标类型获取指标列表
 *
 * 根据当前数据源场景和指标类型（收入或销量），返回对应的指标 key 数组。
 *
 * @param metricType 指标类型（revenue 或 sales_units）
 * @param dataSource 数据源场景（mscience、vgi、both 或 none）
 * @returns 指标 key 数组
 */
export function getMetricsByDataSource(
  metricType: 'revenue' | 'sales_units',
  dataSource: 'mscience' | 'vgi' | 'both' | 'none'
): string[] {
  if (metricType === 'revenue') {
    return getRevenueMetrics(dataSource);
  } else {
    return getSalesUnitsMetrics(dataSource);
  }
}

/**
 * 根据数据源场景获取收入类指标
 *
 * @param dataSource 数据源场景
 * @returns 收入类指标 key 数组
 */
function getRevenueMetrics(dataSource: 'mscience' | 'vgi' | 'both' | 'none'): string[] {
  switch (dataSource) {
    case 'mscience':
      // 仅 M Science：展示总收入、累计总收入以及 M Science 收入
      return [
        'databrain__total_revenue',
        'databrain__lifetime_total_revenue',
        'mscienceLifetimeDigitalRevenue',
      ];
    
    case 'vgi':
      // 仅 VGI：展示总收入、累计总收入以及 Steam 收入
      return [
        'databrain__total_revenue',
        'databrain__lifetime_total_revenue',
        'steamPremiumRevenue',
      ];
    
    case 'both':
    case 'none':
    default:
      // Both sources, no sources, or missing flags: Show only common metrics (default)
      return [
        'databrain__total_revenue',
        'databrain__lifetime_total_revenue',
      ];
  }
}

/**
 * 根据数据源场景获取销量类指标
 *
 * @param dataSource 数据源场景
 * @returns 销量类指标 key 数组
 */
function getSalesUnitsMetrics(dataSource: 'mscience' | 'vgi' | 'both' | 'none'): string[] {
  switch (dataSource) {
    case 'mscience':
      // 仅 M Science：展示基础版本销量、累计销量以及 M Science 数量相关指标
      return [
        'databrain__base_game_sales_units',
        'databrain__lifetime_base_game_sales_units',
        'mscienceLifetimeDigitalUnits',
      ];
    
    case 'vgi':
      // 仅 VGI：展示基础版本销量、累计销量以及 Steam 数量相关指标
      return [
        'databrain__base_game_sales_units',
        'databrain__lifetime_base_game_sales_units',
        'steamUnits',
      ];
    
    case 'both':
    case 'none':
    default:
      // Both sources, no sources, or missing flags: Show only common metrics (default)
      return [
        'databrain__base_game_sales_units',
        'databrain__lifetime_base_game_sales_units',
      ];
  }
}

/**
 * 获取图表使用的可视化指标
 *
 * 根据数据源场景返回适合作为图表展示的指标，通常为表格指标的子集。
 *
 * @param metricType 指标类型（revenue 或 sales_units）
 * @param dataSource 数据源场景
 * @returns 可视化指标 key 数组
 */
function getVisualizationMetrics(
  metricType: 'revenue' | 'sales_units',
  dataSource: 'mscience' | 'vgi' | 'both' | 'none'
): string[] {
  if (metricType === 'revenue') {
    switch (dataSource) {
      case 'mscience':
        return [
          'databrain__lifetime_total_revenue',
          'mscienceLifetimeDigitalRevenue',
        ];
      case 'vgi':
        return [
          'databrain__lifetime_total_revenue',
          'steamPremiumRevenue',
        ];
      case 'both':
      case 'none':
      default:
        // Both sources, no sources, or missing flags: Only common metric (default)
        return [
          'databrain__lifetime_total_revenue',
        ];
    }
  } else {
    // sales_units
    switch (dataSource) {
      case 'mscience':
        return [
          'databrain__lifetime_base_game_sales_units',
          'mscienceLifetimeDigitalUnits',
        ];
      case 'vgi':
        return [
          'databrain__lifetime_base_game_sales_units',
          'steamUnits',
        ];
      case 'both':
      case 'none':
      default:
        // Both sources, no sources, or missing flags: Only common metric (default)
        return [
          'databrain__lifetime_base_game_sales_units',
        ];
    }
  }
}

/**
 * 获取销量类指标对应的分段筛选配置
 *
 * 根据不同数据源场景返回对应的分段配置。
 *
 * @param dataSource 数据源场景
 * @returns 分段筛选配置映射
 */
function getSegmentMapping(dataSource: 'mscience' | 'vgi' | 'both' | 'none'): Record<string, string[]> {
  switch (dataSource) {
    case 'mscience':
      // M Science：使用 Full Game 分段
      return {
        mscience: ['Full Game'],
        gsd: [],
      };
    
    case 'vgi':
      // VGI：使用与 M Science 不同的分段配置
      return {
        mscience: [],
        gsd: [],
        vginsights: [], // VGI doesn't require specific segments
      };
    
    case 'both':
    case 'none':
    default:
      // 两个数据源同时可用、都不可用或标记缺失：使用公共分段配置（默认）
      return {
        mscience: ['Full Game'],
        gsd: [],
      };
  }
}

/**
 * 获取指定指标 key 的默认指标列表
 *
 * 当没有标定数据时使用的辅助方法，根据默认场景返回对应指标数组。
 *
 * @param metricKey 需要获取默认值的指标 key
 * @returns 默认指标 key 数组
 */
export function getDefaultMetrics(metricKey: string): string[] {
  // 默认按两个数据源都可用处理（只展示公共指标）
  const defaultCalibration: ICalibrationAvailability = {
    is_mscience_daily: true,
    is_vgi_daily: true,
  };
  
  const mappings = generateMetricMapping(defaultCalibration);
  return mappings.DATABRAIN_METRIC_MAPPING[metricKey] || [metricKey];
}

/**
 * 动态筛选配置接口
 *
 * `generateDynamicFilterConfig` 返回的配置对象，仅用于指定在查看标定数据时
 * 需要勾选哪些指标
 */
export interface IDynamicFilterConfig {
  /** 查看标定数据时需要勾选的指标 key 列表 */
  metricsToSelect: string[];
}

/**
 * 生成「查看标定原始数据」跳转时使用的动态筛选配置
 *
 * 当用户在指标说明气泡中点击「查看标定原始数据」时，确定需要勾选的指标。
 *
 * 逻辑说明（仅影响勾选哪些指标，不再修改地区）：
 * - 仅 M Science：勾选基础指标 + M Science 指标
 * - 仅 VGI：勾选基础指标 + VGI 指标
 * - 两个数据源同时可用：仅勾选基础指标（默认）
 * - 两个数据源都不可用：仅勾选基础指标（默认）
 * - 没有标定数据：按两个数据源都可用的逻辑处理（默认）
 *
 * @param metricKey 被点击的指标 key（例如 'databrain__total_revenue'）
 * @param calibrationFlags 接口返回的标定数据源可用性标记
 * @returns 动态筛选配置对象
 *
 * @example
 * ```typescript
 * const config = generateDynamicFilterConfig(
 *   'databrain__total_revenue',
 *   { is_mscience_daily: true, is_vgi_daily: false }
 * );
 * // 返回：{
 * //   metricsToSelect: ['databrain__total_revenue', 'mscienceLifetimeDigitalRevenue'],
 * // }
 * ```
 */
export function generateDynamicFilterConfig(
  metricKey: string,
  calibrationFlags: ICalibrationAvailability | null
): IDynamicFilterConfig {
  // 如果没有标定数据，回退为两个数据源都可用的默认逻辑（只展示公共指标）
  if (!calibrationFlags) {
    console.warn('[generateDynamicFilterConfig] No calibration flags provided. Using both-true defaults (common metrics).');
    return {
      metricsToSelect: getMetricsForCalibrationView(metricKey, 'both'),
    };
  }

  const scenario = getDataSourceScenario(calibrationFlags);
  
  console.log('[generateDynamicFilterConfig] Generating filter config:', {
    metricKey,
    calibrationFlags,
    scenario,
  });

  const metricsToSelect = getMetricsForCalibrationView(metricKey, scenario);

  const config: IDynamicFilterConfig = {
    metricsToSelect,
  };

  console.log('[generateDynamicFilterConfig] Generated config:', config);

  return config;
}

/**
 * 获取查看标定数据时需要勾选的指标列表
 *
 * 根据被点击的指标 key 和当前数据源场景，确定需要勾选的所有指标。
 *
 * @param metricKey 被点击的指标 key
 * @param dataSource 数据源场景（mscience、vgi、both 或 none）
 * @returns 需要勾选的指标 key 数组
 */
export function getMetricsForCalibrationView(
  metricKey: string,
  dataSource: 'mscience' | 'vgi' | 'both' | 'none'
): string[] {
  // 判断当前指标是收入类还是销量类
  const isRevenueMetric = metricKey.includes('revenue');
  const isSalesUnitsMetric = metricKey.includes('sales_units');

  if (isRevenueMetric) {
    return getRevenueMetricsForCalibration(metricKey, dataSource);
  } else if (isSalesUnitsMetric) {
    return getSalesUnitsMetricsForCalibration(metricKey, dataSource);
  } else {
    // 未知类型指标，仅返回基础指标自身
    console.warn(`[getMetricsForCalibrationView] Unknown metric type: ${metricKey}`);
    return [metricKey];
  }
}

/**
 * 获取查看标定数据时需要勾选的收入类表格指标
 *
 * 返回「表格指标」需求表中对应的一整组收入类指标。
 *
 * @param metricKey 收入类指标 key
 * @param dataSource 数据源场景
 * @returns 需要勾选的收入类指标 key 数组
 */
function getRevenueMetricsForCalibration(
  metricKey: string,
  dataSource: 'mscience' | 'vgi' | 'both' | 'none'
): string[] {
  switch (dataSource) {
    case 'mscience':
      // 仅 M Science：勾选 4 个表格指标
      // Total Revenue、Lifetime Total Revenue、MS Revenue、MS Lifetime Revenue
      return [
        'databrain__total_revenue',
        'databrain__lifetime_total_revenue',
        'digitalRevenue',  // MScience Digital Revenue($)
        'mscienceLifetimeDigitalRevenue',
      ];
    
    case 'vgi':
      // 仅 VGI：勾选 4 个表格指标
      // Total Revenue、Lifetime Total Revenue、Steam Premium Revenue、Steam Cumulative Revenue
      return [
        'databrain__total_revenue',
        'databrain__lifetime_total_revenue',
        'est_revenue',  // Steam Premium Revenue($)
        'est_cumulative_revenue',  // Steam Cumulative Revenue($)
      ];
    
    case 'both':
    case 'none':
    default:
      // 两个数据源同时可用、都不可用或标记缺失：仅勾选公共指标（2 个，默认）
      // Total Revenue、Lifetime Total Revenue
      return [
        'databrain__total_revenue',
        'databrain__lifetime_total_revenue',
      ];
  }
}

/**
 * 获取查看标定数据时需要勾选的销量类表格指标
 *
 * 返回「表格指标」需求表中对应的一整组销量类指标。
 *
 * @param metricKey 销量类指标 key
 * @param dataSource 数据源场景
 * @returns 需要勾选的销量类指标 key 数组
 */
function getSalesUnitsMetricsForCalibration(
  metricKey: string,
  dataSource: 'mscience' | 'vgi' | 'both' | 'none'
): string[] {
  switch (dataSource) {
    case 'mscience':
      // 仅 M Science：勾选 4 个表格指标
      // Base Game Sales Units、Lifetime Base Game Sales Units、MS Base Game Sales Units、MS Lifetime Base Game Sales Units
      return [
        'databrain__base_game_sales_units',
        'databrain__lifetime_base_game_sales_units',
        'units',  // MScience Digital Units
        'mscienceLifetimeDigitalUnits',
      ];
    
    case 'vgi':
      // 仅 VGI：勾选 4 个表格指标
      // Base Game Sales Units、Lifetime Base Game Sales Units、Steam Units、Steam Cumulative Units
      return [
        'databrain__base_game_sales_units',
        'databrain__lifetime_base_game_sales_units',
        'est_units',  // Steam Units
        'est_cumulative_units',  // Steam Cumulative Units
      ];
    
    case 'both':
    case 'none':
    default:
      // 两个数据源同时可用、都不可用或标记缺失：仅勾选公共指标（2 个，默认）
      // Base Game Sales Units、Lifetime Base Game Sales Units
      return [
        'databrain__base_game_sales_units',
        'databrain__lifetime_base_game_sales_units',
      ];
  }
}

/**
 * 获取查看标定数据时使用的可视化指标
 *
 * 返回在查看标定数据时，可视化下拉框中需要勾选的指标，对应需求表中的「可视化选取」列。
 *
 * @param metricKey 被点击的指标 key
 * @param calibrationFlags 标定数据源可用性标记
 * @returns 可视化指标 key 数组
 */
export function getDynamicVisualizationMetrics(
  metricKey: string,
  calibrationFlags: ICalibrationAvailability | null
): string[] {
  // 如果没有标定数据，使用默认可视化指标配置
  if (!calibrationFlags) {
    return ['databrain__lifetime_total_revenue', 'mscienceLifetimeDigitalRevenue'];
  }

  const scenario = getDataSourceScenario(calibrationFlags);
  const isRevenueMetric = metricKey.includes('revenue');
  const isSalesUnitsMetric = metricKey.includes('sales_units');

  if (isRevenueMetric) {
    return getVisualizationRevenueMetrics(scenario);
  } else if (isSalesUnitsMetric) {
    return getVisualizationSalesUnitsMetrics(scenario);
  } else {
    // Unknown metric type, return defaults
    return ['databrain__lifetime_total_revenue'];
  }
}

/**
 * 根据数据源场景获取收入类可视化指标
 *
 * @param dataSource 数据源场景
 * @returns 收入类可视化指标 key 数组
 */
function getVisualizationRevenueMetrics(
  dataSource: 'mscience' | 'vgi' | 'both' | 'none'
): string[] {
  switch (dataSource) {
    case 'mscience':
      // 仅 M Science：Lifetime Total Revenue、Lifetime MS Revenue
      return [
        'databrain__lifetime_total_revenue',
        'mscienceLifetimeDigitalRevenue',
      ];
    
    case 'vgi':
      // 仅 VGI：Lifetime Total Revenue、Steam Cumulative Revenue
      return [
        'databrain__lifetime_total_revenue',
        'est_cumulative_revenue',
      ];
    
    case 'both':
    case 'none':
    default:
      // Both sources, no sources, or missing flags: Only common metric - DEFAULT
      return [
        'databrain__lifetime_total_revenue',
      ];
  }
}

/**
 * 根据数据源场景获取销量类可视化指标
 *
 * @param dataSource 数据源场景
 * @returns 销量类可视化指标 key 数组
 */
function getVisualizationSalesUnitsMetrics(
  dataSource: 'mscience' | 'vgi' | 'both' | 'none'
): string[] {
  switch (dataSource) {
    case 'mscience':
      // 仅 M Science：Lifetime Base Game Sales Units、MS Lifetime Base Game Sales Units
      return [
        'databrain__lifetime_base_game_sales_units',
        'mscienceLifetimeDigitalUnits',
      ];
    
    case 'vgi':
      // 仅 VGI：Lifetime Base Game Sales Units、Steam Cumulative Units
      return [
        'databrain__lifetime_base_game_sales_units',
        'est_cumulative_units',
      ];
    
    case 'both':
    case 'none':
    default:
      // Both sources, no sources, or missing flags: Only common metric - DEFAULT
      return [
        'databrain__lifetime_base_game_sales_units',
      ];
  }
}
